import {Root, Track, Range, Thumb} from '@radix-ui/react-slider'
import React from 'react'
import styled, {css} from 'styled-components'

import Image from '@/components/image'
import Input from '@/components/input'
import {Stack} from '@/components/stack'
import {Text} from '@/components/text'
import {InfoTooltip} from '@/components/tooltip/info-tooltip'
import {VisuallyHidden} from '@/components/visually-hidden'
import {focusStyles} from '@/styles/utils'
import {radii, colors, tokens} from '@/theme'
import mq from '@/utils/media-query'

import type {ImageSource} from '@/types/image'
import type {SliderProps} from '@radix-ui/react-slider'

import {getValidValue, transformValue, inverseTransformValue, getIconFromValue} from './helpers'

type Variant = keyof Pick<typeof colors.background, 'default' | 'muted'>

export interface Props {
    label?: string
    tooltipText?: string
    value: number
    min?: number
    max?: number
    step?: number
    onChange: (value: number) => void
    onFocus?: () => void
    icons?: [ImageSource, ImageSource, ImageSource]
    withNumberInput?: boolean
    disabled?: boolean
    variant?: Variant
    mode?: 'linear' | 'cubic'
    layout?: 'column' | 'row'
}

const Slider = ({
    label,
    tooltipText,
    icons,
    withNumberInput,
    onChange,
    onFocus,
    min = 0,
    max = 100,
    variant = 'default',
    mode = 'linear',
    step = 1,
    layout = 'column',
    ...props
}: Props) => {
    const id = React.useId()

    const [rawValue, setRawValue] = React.useState(
        inverseTransformValue(props.value, mode, min, max),
    )
    const [inputValue, setInputValue] = React.useState<number | string>(
        getValidValue(props.value, min, max, step),
    )
    const [activeIcon, setActiveIcon] = React.useState<ImageSource | null>(
        getIconFromValue(inputValue as number, min, max, step, icons),
    )

    const handleChange: SliderProps['onValueChange'] = (values) => {
        const raw = values[0]
        const transformed = transformValue(raw, mode, min, max)
        const validValue = getValidValue(transformed, min, max, step)

        setRawValue(raw)
        onChange(validValue)
        setInputValue(validValue)
        setActiveIcon(getIconFromValue(validValue, min, max, step, icons))
    }

    const handleFocusChange = (event: React.FocusEvent<HTMLDivElement>) => {
        if (onFocus && event.type === 'focus') {
            onFocus()
        }
    }

    const handleBlurChange = (event: React.FocusEvent<HTMLInputElement>) => {
        const raw = Number(event.target.value)
        const validValue = getValidValue(raw, min, max, step)

        setRawValue(raw)
        setInputValue(validValue)
        onChange(validValue)
    }

    return (
        <>
            {/* Preload icons by rendering them off screen to make sure they show as soon as you interact with the slider */}
            {icons?.map((icon, index) => (
                <VisuallyHidden
                    key={`icon-${index}`}
                    style={{backgroundImage: `url(${icon.url})`}}
                    aria-hidden="true"
                />
            ))}

            <Stack $space={12} $stretch>
                {label && (
                    <Label id={id} as="label" $isDefaultVariant={variant === 'default'}>
                        {label}
                        {tooltipText && <InfoTooltip label={tooltipText} withSmallIcon={false} />}
                    </Label>
                )}

                <Wrapper
                    $withNumberInput={withNumberInput}
                    $disabled={props.disabled}
                    onFocus={handleFocusChange}
                    onBlur={handleFocusChange}
                    $layout={layout}
                >
                    {activeIcon && icons && <MobileIcon image={activeIcon} />}

                    <SliderRoot
                        {...props}
                        value={[rawValue]}
                        min={min}
                        max={max}
                        onValueChange={handleChange}
                    >
                        <SliderTrack $variant={variant}>
                            <SliderRange />
                        </SliderTrack>
                        <SliderThumb $iconUrl={activeIcon?.url || ''} title="slider" />
                    </SliderRoot>

                    {withNumberInput && (
                        <StyledInput
                            type="number"
                            $variant={variant}
                            min={min}
                            max={max}
                            disabled={props.disabled}
                            step={step}
                            value={inputValue}
                            onFocus={handleFocusChange}
                            onChange={setInputValue}
                            onBlur={handleBlurChange}
                            aria-labelledby={id}
                        />
                    )}
                </Wrapper>
            </Stack>
        </>
    )
}

const mobileBreakpoint = 'sm'

const StyledInput = styled(Input)<{$variant: Variant}>`
    padding-right: ${tokens.spacing10};
    padding-left: ${tokens.spacing10};
    background-color: ${({$variant, theme}) => $variant === 'muted' && theme.tokens.shade000};

    /* Override the default disabled Input styles so we can style this whole component collectively */
    &:disabled {
        color: ${colors.textInput.default.foreground};
        background: ${colors.textInput.default.background};
    }

    &:focus {
        ${focusStyles()}
        border-radius: ${tokens.arc4};
    }
`

interface WrapperProps {
    $withNumberInput?: Props['withNumberInput']
    $disabled?: Props['disabled']
    $layout: Props['layout']
}

const Wrapper = styled.div<WrapperProps>(({$withNumberInput, $disabled, $layout}) => {
    const withNumberInputStyles = css`
        grid-template-columns: ${$layout === 'column' ? '1fr' : '1fr 100px'};

        input[type='number'] {
            text-align: center;
        }

        ${mq.min(mobileBreakpoint)} {
            grid-template-columns: 1fr 100px;
        }
    `

    const disabledStyles = css`
        opacity: 0.25;
    `

    return css`
        display: grid;
        width: 100%;
        row-gap: ${tokens.spacing10};
        column-gap: ${tokens.spacing24};
        align-items: center;
        justify-items: center;

        ${$withNumberInput && withNumberInputStyles}
        ${$disabled && disabledStyles}
    `
})

const Label = styled(Text)<{$isDefaultVariant: boolean}>`
    font-size: ${({$isDefaultVariant, theme}) => $isDefaultVariant && theme.tokens.fontLarge};
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;

    & button {
        flex-shrink: 0;
        margin-top: ${({theme}) => theme.tokens.spacing6};
    }

    ${mq.min(mobileBreakpoint)} {
        align-items: center;

        & button {
            margin-top: 0;
        }
    }
`

const SliderRoot = styled(Root)`
    position: relative;
    display: flex;
    align-items: center;
    user-select: none;
    touch-action: none;
    width: 100%;
    max-width: 100%;
    height: ${tokens.spacing8};
    padding-top: 30px;
    padding-bottom: 30px;
    cursor: pointer;

    ${mq.min(mobileBreakpoint)} {
        height: ${tokens.spacing4};
    }
`

const SliderTrack = styled(Track)<{$variant: Variant}>`
    position: relative;
    flex-grow: 1;
    height: inherit;
    background-color: ${({$variant, theme}) =>
        $variant === 'default' ? theme.tokens.shade300 : theme.tokens.shade700};
    border-radius: 0;
`

const SliderRange = styled(Range)`
    position: absolute;
    background: ${tokens.shade900};
    height: ${tokens.spacing8};

    ${mq.min(mobileBreakpoint)} {
        height: ${tokens.spacing4};
    }
`

interface SliderHandleProps {
    $iconUrl: string
}

const SliderThumb = styled(Thumb)<SliderHandleProps>`
    display: block;
    width: ${tokens.spacing24};
    height: ${tokens.spacing24};
    background-color: ${tokens.shade000};
    border: 2px solid ${tokens.shade900};
    border-radius: ${radii.circle};

    ${mq.min(mobileBreakpoint)} {
        width: ${tokens.spacing16};
        height: ${tokens.spacing16};
    }

    &:focus {
        border: 2px solid ${tokens.blue700};
        outline: none;

        &::after {
            position: absolute;
            top: calc(100% + ${tokens.spacing16});
            left: 50%;
            display: block;
            width: 45px;
            height: 45px;
            pointer-events: none;
            content: '';
            background-image: url('${(props) => props.$iconUrl}');
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
            transition: background ${tokens.smoothInOut};
            transform: translateX(-50%);

            ${mq.max(mobileBreakpoint)} {
                display: none;
            }
        }
    }

    &::before {
        position: absolute;
        top: 50%;
        left: 50%;
        width: ${tokens.spacing44};
        height: ${tokens.spacing44};
        content: '';
        background: transparent;
        border-radius: inherit;
        transform: translate(-50%, -50%);
    }
`

const MobileIcon = styled(Image)`
    width: 80px;
    height: 80px;

    ${mq.min(mobileBreakpoint)} {
        display: none;
    }
`

export default Slider
